
import React, { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import styles from './SummaryPrompt.module.css';
import Icon from '../common/Icon';
import DataProvider from '../../data_provider/DataProvider';
import { SummaryReqBody, AIGateway } from '../../data_provider/gateways/AIGateway';

interface SummaryPromptProps {
    isVisible: boolean;
    mapData: any[]; // 지도 데이터
    onClose: () => void;
    onSummaryComplete?: (summary: string) => void;
    initialSummary?: string;
    initialCompleted?: boolean;
    processIdx?: number; // 프로세스 인덱스 추가
}

// 요약 상태 타입 정의
type SummaryStatus =
    | 'waiting'           // 대기중 (시작 전)
    | 'connecting'        // 연결중
    | 'typing'           // 타이핑중 (결과 출력중)
    | 'completed'        // 완료
    | 'paused'           // 일시정지 (타이핑 중 정지)

/**
 * 요약 프롬프트 컴포넌트
 * @component SummaryPrompt 
 * @author Kim Young Jun
 * @date 2025-09-19
 */
const SummaryPrompt: React.FC<SummaryPromptProps> = ({
    isVisible,
    mapData,
    onSummaryComplete,
    initialSummary = '',
    initialCompleted = false,
    processIdx
}) => {
    const currentAIGateway = useRef<AIGateway | null>(null);
    const [status, setStatus] = useState<SummaryStatus>(
        initialCompleted ? 'completed' : 'waiting'
    );
    const [summaryResult, setSummaryResult] = useState<string>(initialSummary);
    const summaryResultRef = useRef<string>(initialSummary);
    const [showCopyMessage, setShowCopyMessage] = useState(false);
    const connectingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [isRestarting, setIsRestarting] = useState(false);

    // processIdx 변경 시 스트림 중단
    useEffect(() => {
        if (processIdx !== 0 && (status === 'connecting' || status === 'typing')) {
            console.log('ProcessIdx 변경으로 인한 요약 스트림 중단');

            // 연결 중 상태에서 타임아웃 클리어
            if (connectingTimeoutRef.current) {
                clearTimeout(connectingTimeoutRef.current);
                connectingTimeoutRef.current = null;
            }

            // AI Gateway 스트림 중단
            if (currentAIGateway.current) {
                currentAIGateway.current.abortStream();
                currentAIGateway.current = null;
            }

            // 상태를 일시정지로 변경
            setStatus('paused');
        }
    }, [processIdx, status]);

    // AI 서버에 요약 요청 (SSE 스트리밍)
    const requestSummary = async () => {
        console.log('=== 새로운 요약 요청 시작 ===');

        // 1. 결과 상태 초기화
        setSummaryResult('');
        summaryResultRef.current = '';
        // isRestarting은 나중에 해제 (재시작 플래그 유지)

        // 2. 이전 인스턴스 완전 제거
        if (currentAIGateway.current) {
            console.log('이전 AIGateway 인스턴스 정리 중...');
            try {
                currentAIGateway.current.abortStream();
            } catch (e) {
                console.log('이전 인스턴스 정리 완료');
            }
            currentAIGateway.current = null;
        }

        // 3. 새로운 인스턴스 생성
        console.log('새로운 AIGateway 인스턴스 생성');
        currentAIGateway.current = new AIGateway();

        setStatus('connecting');

        try {
            // 4. 연결 대기
            await new Promise((resolve) => {
                connectingTimeoutRef.current = setTimeout(resolve, 1000);
            });

            // 5. 진행 상태 체크 (connecting timeout이 완료된 후)

            setStatus('typing');

            // 6. 요청 데이터 준비
            const requestBody: SummaryReqBody = {
                // mapData: [
                //     Object.keys(mapData[0] || {}), // 헤더
                //     ...mapData.slice(0, 10).map(row => Object.values(row)) // 샘플 데이터 (최대 10개)
                // ],
                // dataCount: mapData.length,
                // fields: Object.keys(mapData[0] || {}),
                // hmchoi
                mapData: mapData.slice(0, 11), // 헤더
                dataCount: Math.max(mapData.length - 1, 0), // 헤더 제외 샘플 데이터 (최대 10개)
                fields: mapData[0] || [],
                type: 'summary' // 요약 요청 타입 지정
            };

            console.log('요청 데이터:', requestBody);
            console.log('AIGateway 요청 시작...');

            // 7. 새로운 인스턴스로 SSE 스트림 요청
            await currentAIGateway.current!.reqSummaryStream(requestBody, {
                onChunk: (chunk: string) => {
                    // 디버깅: 받은 청크 로그
                    if (chunk === '\n') {
                        console.log('SummaryPrompt: 개행문자 받음 -> 화면에 줄바꿈 적용');
                    } else if (chunk === '\t') {
                        console.log('SummaryPrompt: 탭문자 받음 -> 화면에 탭 적용');
                    } else if (chunk === ' ') {
                        console.log('SummaryPrompt: 공백문자 받음 -> 화면에 공백 적용');
                    } else {
                        console.log(`SummaryPrompt received: '${chunk}'`);
                    }

                    // useRef로 즉시 업데이트하고 flushSync로 강제 동기 렌더링
                    summaryResultRef.current = summaryResultRef.current + chunk;
                    flushSync(() => {
                        setSummaryResult(summaryResultRef.current);
                    });
                },
                onComplete: () => {
                    // 완료 상태 (모든 타이핑이 끝난 후)
                    setStatus('completed');
                    setIsRestarting(false); // 요청 완료 시 재시작 플래그 해제
                    onSummaryComplete?.(summaryResultRef.current);
                },
                onError: (error: string) => {
                    console.error('요약 요청 실패:', error);
                    // 에러 시 샘플 데이터로 fallback
                    const sampleSummary = generateFailSummary();
                    summaryResultRef.current = sampleSummary;
                    setSummaryResult(sampleSummary);
                    setStatus('completed');
                    setIsRestarting(false); // 에러 시에도 재시작 플래그 해제
                    onSummaryComplete?.(sampleSummary);
                }
            });

        } catch (error: any) {
            console.error('요약 요청 실패:', error);
            // 에러 시 샘플 데이터로 fallback
            const sampleSummary = generateFailSummary();
            summaryResultRef.current = sampleSummary;
            setSummaryResult(sampleSummary);
            setStatus('completed');
            setIsRestarting(false); // catch 에러 시에도 재시작 플래그 해제
            onSummaryComplete?.(sampleSummary);
        }
    };

    // 요약 프로세스 제어
    const handleInteraction = () => {
        switch (status) {
            case 'waiting':
                requestSummary();
                break;

            case 'connecting':
                // 연결 중 정지 - 타임아웃 클리어하고 stopped 상태로
                if (connectingTimeoutRef.current) {
                    clearTimeout(connectingTimeoutRef.current);
                    connectingTimeoutRef.current = null;
                }
                setStatus('paused');
                break;

            case 'typing':
                // 타이핑 중 정지 - 인스턴스 완전 제거
                if (currentAIGateway.current) {
                    currentAIGateway.current.abortStream();
                    currentAIGateway.current = null;
                }
                setStatus('paused');
                break;

            case 'paused':
                // 정지/일시정지 상태에서 재시작
                console.log('paused 상태에서 재시작 시작');
                setIsRestarting(true);
                setStatus('waiting'); // 상태를 먼저 waiting으로 변경
                summaryResultRef.current = '';
                setSummaryResult(''); // 이전 결과 초기화

                // 연결 상태 리셋을 위한 짧은 딜레이
                setTimeout(() => {
                    requestSummary();
                }, 100);
                break;
        }
    };

    // 요약 결과 복사
    const copySummary = () => {
        if (summaryResult) {
            navigator.clipboard.writeText(summaryResult).then(() => {
                console.log('요약 결과가 클립보드에 복사되었습니다.');
                setShowCopyMessage(true);
                setTimeout(() => {
                    setShowCopyMessage(false);
                }, 1500);
            }).catch(err => {
                console.error('복사 실패:', err);
            });
        }
    };

    // 요약 실패 데이터 생성
    const generateFailSummary = () => {
        const totalCount = mapData.length;
        const fields = mapData.length > 0 ? Object.keys(mapData[0]) : [];

        return '요약 데이터 생성에 실패했습니다.'
    };

    if (!isVisible) return null;

    return (
        <div className={`${styles.container}`}>
            <div className={styles.promptHeader}>
                <div className={styles.promptInteractionContainer}>
                    <div className={`${styles.promptProfile} ${(status === 'connecting' || status === 'typing') ? styles.active : ''}`}>
                        {status === 'connecting' || status === 'typing' ? (
                            <div className={styles.dotsContainer}>
                                <div className={styles.dotElastic}></div>
                            </div>
                        ) : (
                            <img className={styles.aiAgentLogo} src={require("../../img/ai_agent_logo.png")} alt="AI Agent Logo" />
                        )}
                    </div>
                    <div className={`${styles.interactionText} ${(status === 'connecting' || status === 'typing') ? styles.animating : ''}`}>
                        {status === 'waiting'
                            ? "업로드한 지도 데이터를 인공지능을 활용해 요약할 수 있습니다. 요약을 원하시면 오른쪽에 위치한 버튼을 눌러주세요 😊"
                            : status === 'connecting'
                            ? (isRestarting ? "데이터를 다시 확인중입니다." : "데이터를 확인중입니다.")
                            : status === 'typing'
                            ? "요약중입니다."
                            : status === 'paused'
                            ? "지도 데이터 요약을 정지했습니다. 다시 요약을 원하시면 오른쪽에 위치한 버튼을 눌러주세요 😊"
                            : "지도 데이터 요약이 완료되었습니다 😊"
                        }
                    </div>
                </div>
                <div className={styles.propmptControlContainer}>
                    {showCopyMessage && (
                        <div className={styles.copyMessage}>
                            복사가 완료되었습니다.
                        </div>
                    )}
                    {/* {status === 'completed' ? (
                        <button
                            className={styles.copyButton}
                            onClick={copySummary}
                        >
                            <Icon
                                className={styles.copyIcon}
                                name="copy"
                                size='small'
                                size2='medium'
                                color='white'
                            />
                        </button>
                    ) : null} */}
                    <button
                        className={`${styles.interactionButton} ${status !== 'completed' ? styles.interactionButtonActive : ''}`}
                        onClick={handleInteraction}
                        style={{ cursor: status !== 'completed' ? 'pointer' : 'default' }}
                    >
                        <Icon
                            className={styles.interactionIcon}
                            name={
                                status === 'waiting' ? "arrow_play" :
                                status === 'connecting' || status === 'typing' ? "pause" :
                                status === 'paused' ? "arrow_play" : "complete"
                            }
                            size={status === 'connecting' || status === 'typing' ? 'xsmall' : 'small'}
                            color='var(--main-color)'
                        />
                    </button>
                </div>
            </div>
                {(status === 'typing' || status === 'completed' || (status === 'paused' && summaryResult.trim())) ? (
                    <>
                        {/* 요약 결과 표시 */}
                        <div className={styles.resultSection}>
                            <div className={styles.resultContent}>
                                <div className={styles.resultLine}>
                                    {summaryResult}
                                </div>
                            </div>
                        </div>
                    </>
                ) : null}
        </div>
    );
};

export default SummaryPrompt;